import { shallowEqual } from 'react-redux';

import { Anchor, List } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import accessUtils from '../../../../utils/accessUtils';

import SingleFamilyCSEvents from '../../../../model/ClickstreamEvent/TotalMonthlyPriceWithFee/SingleFamilyCSEvents';

const WizardTipContent = (): JSX.Element => {
  const {
    appConfig,
    appConfigCanAccessTotalMonthlyPriceWithFee,
    cookieEnabledServices,
    currentProperty,
    isOptInTotalMonthlyPriceWithFeeEligible,
    isTotalMonthlyPriceWithFeeEligible,
    user,
  } = useSelector((state) => {
    return {
      appConfig: state?.app?.appConfig,
      appConfigCanAccessTotalMonthlyPriceWithFee: state.app.appConfig.config.features.canAccessTotalMonthlyPriceWithFee,
      cookieEnabledServices: state.app.cookieEnabledServices,
      currentProperty: state?.properties?.currentProperty,
      isOptInTotalMonthlyPriceWithFeeEligible:
        state.properties.currentProperty?.totalMonthlyPriceWithFee.isOptInEligible,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      user: state?.user,
    };
  }, shallowEqual);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    canAccessTotalMonthlyPriceWithFee: appConfigCanAccessTotalMonthlyPriceWithFee,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const canAccessUpdatedTotalMonthlyPriceCopy = accessUtils.canAccessUpdatedTotalMonthlyPriceCopy({
    appConfig,
    cookieEnabledServices,
  });

  return (
    <div data-testid="WizardTipContent">
      <List.Root appearance="bullet">
        {canAccessTotalMonthlyPriceWithFee || isOptInTotalMonthlyPriceWithFeeEligible ? (
          <>
            {canAccessTotalMonthlyPriceWithFee && (
              <>
                <List.Item>
                  Make sure to add any required monthly fees, and note if any are already included in the base rent.
                </List.Item>

                <List.Item>
                  Fee transparency helps renters make informed decisions and improves efficiency for you when placing a
                  renter.{' '}
                  <Anchor
                    target="_blank"
                    href="https://www.zillow.com/rentals-network/pricing-transparency/"
                    onClick={() => {
                      SingleFamilyCSEvents.LearnMore({
                        user,
                        currentProperty,
                        triggerObject: 'costs_and_fees',
                        triggerLocation: 'posting_path',
                      });
                    }}
                    data-eventid={SingleFamilyCSEvents.LearnMore.data_event_id}
                  >
                    Learn more
                  </Anchor>{' '}
                  about regulations in your area.
                </List.Item>
              </>
            )}

            {isOptInTotalMonthlyPriceWithFeeEligible && (
              <>
                <List.Item>Required monthly fees can be included in your base rent or added separately here.</List.Item>

                <List.Item>
                  Once your required monthly fees are accounted for, we suggest opting to show the{' '}
                  {canAccessUpdatedTotalMonthlyPriceCopy ? 'total monthly price' : 'total price'} on your listing.
                </List.Item>
              </>
            )}

            <List.Item>Wait to add application fees — we&apos;ll ask about those last.</List.Item>
          </>
        ) : (
          <>
            <List.Item>Wait to add application fees — we&apos;ll ask about those last.</List.Item>

            <List.Item>Only add the fees you need, and note which are included in the base rent.</List.Item>

            <List.Item>
              Some states require disclosing certain fees — be sure to check what&apos;s required for your area.
            </List.Item>
          </>
        )}
      </List.Root>
    </div>
  );
};

export default WizardTipContent;
